import { getLocaleText } from "shared/boot/i18n";

interface AttachmentBlob {
  relativeUrl?: string;
  video?: boolean;
}

interface AttachmentMetadata {
  duration?: number;
  height: number;
  width: number;
}

function isAbsoluteUrl(url?: string): boolean {
  return (url?.startsWith("http://") || url?.startsWith("https://")) ?? false;
}

export function attachmentUrl(blob: AttachmentBlob): string {
  const { relativeUrl: url } = blob;

  if (!url) {
    throw new Error(getLocaleText("helpers.attachment.could_not_load_image"));
  }

  if (isAbsoluteUrl(url)) return url;

  return `${import.meta.env.VITE_API_BASE_URL}${url}`;
}

export async function getAttachmentMetadata(
  blob: AttachmentBlob
): Promise<AttachmentMetadata> {
  try {
    if (blob.video) {
      const video = await new Promise<HTMLVideoElement>((resolve) => {
        const videoElement = document.createElement("video");
        videoElement.preload = "metadata";
        videoElement.onloadedmetadata = () => resolve(videoElement);
        videoElement.src = attachmentUrl(blob);
      });

      return {
        duration: video.duration,
        height: video.videoHeight,
        width: video.videoWidth,
      };
    }

    const image = new Image();
    image.src = attachmentUrl(blob);

    await image.decode();

    return {
      height: image.height,
      width: image.width,
    };
  } catch {
    throw new Error(getLocaleText("helpers.attachment.could_not_load_image"));
  }
}
